import type { CSSProperties } from "react";

/** Card with 14px radius, deep shadow, thin border */
export const card: CSSProperties = {
  border: "1px solid var(--color-border)",
  borderRadius: "14px",
  padding: "1.5rem 1.75rem",
  background: "var(--color-surface)",
  boxShadow: "0 4px 32px var(--color-shadow)",
};

/** Centered content column, standard width */
export const pageContainer: CSSProperties = {
  maxWidth: "680px",
  margin: "0 auto",
};

/** Centered content column, wider (tables etc.) */
export const widePageContainer: CSSProperties = {
  maxWidth: "960px",
  margin: "0 auto",
};

/** Narrow centred column for simple forms */
export const narrowPageContainer: CSSProperties = {
  maxWidth: "480px",
  margin: "0 auto",
};

/** Solid-accent primary action / confirmation button */
export const primaryBtn: CSSProperties = {
  padding: "0.55rem 1.3rem",
  fontSize: "0.88rem",
  fontWeight: 600,
  fontFamily: "var(--font-body)",
  borderRadius: "8px",
  border: "none",
  background: "var(--color-accent)",
  color: "var(--color-btn-primary-text)",
  cursor: "pointer",
  letterSpacing: "0.01em",
  // 44px is the minimum comfortable touch target (CLAUDE.md). display:inline-flex so the
  // extra height actually centers the content rather than padding out below it.
  minHeight: 44,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

/** Raised secondary / information button */
export const secondaryBtn: CSSProperties = {
  padding: "0.55rem 1.3rem",
  fontSize: "0.88rem",
  fontWeight: 500,
  fontFamily: "var(--font-body)",
  borderRadius: "8px",
  border: "1px solid var(--color-accent)",
  background: "var(--color-btn-secondary-bg)",
  color: "var(--color-btn-secondary-text)",
  cursor: "pointer",
  minHeight: 44,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

/** Outlined danger button */
export const dangerBtn: CSSProperties = {
  padding: "0.4rem 0.9rem",
  fontSize: "0.85rem",
  fontWeight: 500,
  borderRadius: "8px",
  border: "1px solid var(--color-danger)",
  background: "transparent",
  color: "var(--color-danger)",
  cursor: "pointer",
};

/** Filled danger button (destructive primary action) */
export const dangerFilledBtn: CSSProperties = {
  padding: "0.55rem 1.3rem",
  fontSize: "0.88rem",
  fontWeight: 600,
  borderRadius: "8px",
  border: "none",
  background: "var(--color-danger)",
  color: "#fff",
  cursor: "pointer",
  minHeight: 44,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

/** Outlined accent button, used for the Export action everywhere it appears */
export const exportBtn: CSSProperties = {
  padding: "7px 10px",
  fontSize: 12,
  fontWeight: 500,
  fontFamily: "var(--font-body)",
  borderRadius: 8,
  border: "1px solid var(--color-accent)",
  background: "none",
  color: "var(--color-accent)",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: 6,
};

/** Compact action button */
export const smallBtn: CSSProperties = {
  padding: "0.3rem 0.7rem",
  fontSize: "0.8rem",
  fontWeight: 500,
  borderRadius: "8px",
  border: "1px solid var(--color-border)",
  background: "var(--color-surface-alt)",
  color: "var(--color-text)",
  cursor: "pointer",
};

/** Text input */
export const inputStyle: CSSProperties = {
  padding: "0.6rem 0.9rem",
  fontSize: "0.93rem",
  borderRadius: "12px",
  border: "1px solid var(--color-input-border)",
  background: "var(--color-input-bg)",
  color: "var(--color-input-text)",
  width: "100%",
  boxSizing: "border-box",
  outline: "none",
};

/** Select / dropdown */
export const selectStyle: CSSProperties = {
  padding: "0.6rem 2.2rem 0.6rem 0.9rem",
  fontSize: "0.93rem",
  borderRadius: "12px",
  border: "1px solid var(--color-input-border)",
  background: "var(--color-input-bg)",
  color: "var(--color-input-text)",
  outline: "none",
  cursor: "pointer",
  appearance: "none" as CSSProperties["appearance"],
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23888' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 0.75rem center",
  backgroundSize: "10px 6px",
};

/** Full-width data table */
export const tableStyle: CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
};

/** Table header cell — uppercase tiny tracked letters, generous padding */
export const thStyle: CSSProperties = {
  textAlign: "left",
  padding: "0.65rem 1rem",
  borderBottom: "1px solid var(--color-border)",
  fontSize: "0.72rem",
  color: "var(--color-text-muted)",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  whiteSpace: "nowrap",
};

/** Table data cell — more vertical breathing room */
export const tdStyle: CSSProperties = {
  padding: "0.8rem 1rem",
  borderBottom: "1px solid var(--color-border-subtle)",
  verticalAlign: "middle",
  fontSize: "0.9rem",
};

/** Section heading label — uppercase, muted, small caps style */
export const sectionLabel: CSSProperties = {
  fontSize: "0.72rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: "var(--color-text-muted)",
  margin: "0 0 0.85rem",
};

/** Standard page title — display font, large, bold */
export const pageTitle: CSSProperties = {
  margin: 0,
  fontFamily: "var(--font-display)",
  fontSize: "2rem",
  fontWeight: 800,
  letterSpacing: "0.01em",
  lineHeight: 1.1,
  color: "var(--color-text)",
};

/** Card/panel section header — DM Mono, uppercase, tracked. No divider beneath it. */
export const panelHeading: CSSProperties = {
  margin: "0 0 1rem",
  fontSize: "0.95rem",
  fontWeight: 500,
  color: "var(--color-text)",
  fontFamily: "var(--font-mono)",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};

/** Inline error message */
export const errorStyle: CSSProperties = {
  color: "var(--color-danger)",
  fontSize: "0.9rem",
  margin: "0.25rem 0",
};

/**
 * Returns styles for a tab button — pill style.
 * Active uses solid accent fill; inactive is transparent with muted text.
 */
export function tabBtnStyle(active: boolean): CSSProperties {
  return {
    padding: "0.45rem 1.1rem",
    fontSize: "0.88rem",
    fontWeight: active ? 600 : 500,
    borderRadius: "10px",
    border: active ? "none" : "1px solid var(--color-border)",
    background: active ? "var(--color-accent)" : "transparent",
    color: active ? "var(--color-btn-primary-text)" : "var(--color-text-muted)",
    cursor: "pointer",
  };
}

/** Button styled as a link (for clickable player names etc.) */
export const playerNameBtn: CSSProperties = {
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: 0,
  color: "var(--color-accent)",
  fontSize: "inherit",
  fontFamily: "inherit",
  fontWeight: 500,
};

/** Full-screen semi-transparent backdrop with blur */
export const backdrop: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.6)",
  backdropFilter: "blur(4px)",
  zIndex: 200,
};

/** Centred modal card */
export const modal: CSSProperties = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "min(560px, 92vw)",
  maxHeight: "85vh",
  overflowY: "auto",
  background: "var(--color-surface)",
  borderRadius: "16px",
  border: "1px solid var(--color-border)",
  padding: "2rem",
  boxSizing: "border-box",
  zIndex: 201,
  boxShadow: "0 24px 64px var(--color-shadow)",
};

/** Small status / label badge */
export const badge: CSSProperties = {
  display: "inline-block",
  padding: "0.2rem 0.6rem",
  borderRadius: "999px",
  fontSize: "0.72rem",
  fontWeight: 600,
  letterSpacing: "0.02em",
};
